/**
 * ファイル処理サービス
 * ファイルの検証、変換、ユーティリティ関数を集約
 */

import { i18n } from '../i18n/config';
import type { FileAttachment as FileAttachmentType } from '../types';

// ==================== 定数 ====================

export const DEFAULT_MAX_FILES = 5;
export const DEFAULT_MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const DEFAULT_ACCEPTED_TYPES = [
  // 画像
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'image/bmp',
  'image/x-icon',
  // ドキュメント
  'application/pdf',
  // テキスト系
  'text/plain',
  'text/csv',
  'text/tab-separated-values',
  'text/markdown',
  // マークアップ/データ
  'text/xml',
  'application/xml',
  'text/html',
  'application/json',
  'application/x-yaml',
  'text/yaml',
  // プログラミング言語
  'text/javascript',
  'application/javascript',
  'text/typescript',
  'text/x-python',
  'text/x-java-source',
  'text/x-c',
  'text/x-c++src',
  'text/css',
  'text/x-scss',
  'text/x-less',
  'application/x-sh',
  'text/x-sql',
  'text/x-ruby',
  'text/x-go',
  'text/x-rust',
  'text/x-swift',
  'text/x-kotlin',
  'application/x-php',

  // プログラミング教育用ファイル（ZIPベース: SB3, MC2など）
  'application/zip',
  'application/x-zip-compressed',
  '.sb3', // Scratch 3.0
  '.mc2', // マイクラッチ
];

// 画像生成で受け付けるファイルタイプ
export const IMAGE_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
export const IMAGE_MAX_FILES = 3;

// ==================== 型定義 ====================

export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

export interface FilesToAttachmentsResult {
  attachments: FileAttachmentType[];
  error?: string;
}

// ==================== ファイル検証 ====================

/**
 * 単一ファイルの検証
 */
export function validateFile(
  file: File,
  maxFileSize: number = DEFAULT_MAX_FILE_SIZE,
  acceptedTypes: string[] = DEFAULT_ACCEPTED_TYPES
): FileValidationResult {
  // ファイルサイズチェック
  if (file.size > maxFileSize) {
    const maxMB = (maxFileSize / (1024 * 1024)).toFixed(1);
    return {
      isValid: false,
      error: i18n.t('fileAttachment.error.fileTooLarge', { filename: file.name, maxSize: maxMB }),
    };
  }

  // MIMEタイプまたは拡張子チェック
  const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
  const isValidMimeType = acceptedTypes.includes(file.type);
  const isValidExtension = acceptedTypes.includes(fileExtension);

  if (!isValidMimeType && !isValidExtension) {
    return {
      isValid: false,
      error: i18n.t('fileAttachment.error.invalidType', { filename: file.name }),
    };
  }

  return { isValid: true };
}

/**
 * 画像ファイルかどうかを判定
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * 許可された画像ファイルかどうかを判定
 */
export function isAcceptedImageFile(file: File): boolean {
  return IMAGE_ACCEPTED_TYPES.includes(file.type);
}

// ==================== ファイル変換 ====================

/**
 * ファイルをBase64文字列に変換
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // "data:image/png;base64," の部分を削除
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
}

/**
 * 拡張子からMIMEタイプを推測（ブラウザがMIMEタイプを判定できない場合のフォールバック）
 */
export function guessMimeTypeFromExtension(filename: string): string {
  const ext = '.' + filename.split('.').pop()?.toLowerCase();
  const extensionToMimeType: Record<string, string> = {
    // 画像
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.gif': 'image/gif',
    '.webp': 'image/webp',
    '.svg': 'image/svg+xml',
    '.bmp': 'image/bmp',
    '.ico': 'image/x-icon',
    // ドキュメント
    '.pdf': 'application/pdf',
    // テキスト系
    '.txt': 'text/plain',
    '.log': 'text/plain',
    '.csv': 'text/csv',
    '.tsv': 'text/tab-separated-values',
    '.md': 'text/markdown',
    '.markdown': 'text/markdown',
    // マークアップ/データ
    '.xml': 'text/xml',
    '.html': 'text/html',
    '.htm': 'text/html',
    '.json': 'application/json',
    '.yaml': 'application/x-yaml',
    '.yml': 'application/x-yaml',
    // プログラミング言語
    '.js': 'text/javascript',
    '.mjs': 'text/javascript',
    '.ts': 'text/typescript',
    '.py': 'text/x-python',
    '.java': 'text/x-java-source',
    '.c': 'text/x-c',
    '.h': 'text/x-c',
    '.cpp': 'text/x-c++src',
    '.cc': 'text/x-c++src',
    '.cxx': 'text/x-c++src',
    '.hpp': 'text/x-c++src',
    '.css': 'text/css',
    '.scss': 'text/x-scss',
    '.less': 'text/x-less',
    '.sh': 'application/x-sh',
    '.bash': 'application/x-sh',
    '.sql': 'text/x-sql',
    '.rb': 'text/x-ruby',
    '.go': 'text/x-go',
    '.rs': 'text/x-rust',
    '.swift': 'text/x-swift',
    '.kt': 'text/x-kotlin',
    '.kts': 'text/x-kotlin',
    '.php': 'application/x-php',
    // プログラミング教育用ファイル（ZIPベース）
    '.zip': 'application/zip',
    '.sb3': 'application/zip', // Scratch 3.0
    '.mc2': 'application/zip', // マイクラッチ
  };
  return extensionToMimeType[ext] || '';
}

/**
 * ファイル配列をFileAttachmentType配列に変換
 */
export async function filesToAttachments(
  files: File[],
  maxFileSize: number = DEFAULT_MAX_FILE_SIZE,
  acceptedTypes: string[] = DEFAULT_ACCEPTED_TYPES,
  maxFiles: number = DEFAULT_MAX_FILES,
  currentFileCount: number = 0
): Promise<FilesToAttachmentsResult> {
  // ファイル数チェック
  if (files.length + currentFileCount > maxFiles) {
    return {
      attachments: [],
      error: i18n.t('fileAttachment.error.tooManyFiles', { maxFiles }),
    };
  }

  const validFiles: File[] = [];
  for (const file of files) {
    const validation = validateFile(file, maxFileSize, acceptedTypes);
    if (!validation.isValid) {
      return { attachments: [], error: validation.error };
    }
    validFiles.push(file);
  }

  try {
    const attachments: FileAttachmentType[] = await Promise.all(
      validFiles.map(async (file) => {
        const base64 = await fileToBase64(file);
        // MIMEタイプが空の場合は拡張子から推測（.sb3, .mc2などブラウザが認識できないファイル用）
        const mimeType = file.type || guessMimeTypeFromExtension(file.name);
        return {
          data: base64,
          filename: file.name,
          mimeType,
        };
      })
    );
    return { attachments };
  } catch {
    return {
      attachments: [],
      error: i18n.t('fileAttachment.error.readError'),
    };
  }
}

// ==================== ユーティリティ ====================

/**
 * ファイルサイズを人間が読める形式にフォーマット
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * 画像ソースURLを取得（Base64またはURL）
 */
export function getImageSrc(image: { image_url?: string; image_base64?: string }): string | null {
  if (image.image_base64) {
    return `data:image/png;base64,${image.image_base64}`;
  }
  if (image.image_url) {
    return image.image_url;
  }
  return null;
}

/**
 * 画像をダウンロード
 */
export async function downloadImage(imageSrc: string, filename: string): Promise<void> {
  try {
    // Base64データURLの場合はそのまま使用
    if (imageSrc.startsWith('data:')) {
      const link = document.createElement('a');
      link.href = imageSrc;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // 外部URLの場合はfetchしてBlobに変換
    const response = await fetch(imageSrc);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // メモリリークを防ぐためにBlobURLを解放
    URL.revokeObjectURL(blobUrl);
  } catch {
    // フォールバック: 同じタブで開く（iOS対応）
    window.open(imageSrc, '_self');
  }
}
